'use client';
import React, { useEffect } from 'react';
import { classnames } from '@/utils/classnames/classnames';
import { Transition } from '@headlessui/react';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import useScrollBlock from '@/hooks/useScrollBlock';
import Button from '@/components/atoms/button';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import { DrawerProps } from './types';
import useClassNames from './hooks/useClassNames';
import Overlay from '../../atoms/overlay';
import { useProductList } from '../product-list/context';
import Typography from '@/components/atoms/typography';
import type { DrawerVariant } from './types';
// import { usePathname } from 'next/navigation';

const Drawer = ({
  className = '',
  headerClassName,
  drawerWrapperClassName1,
  isOpen,
  overlay = true,
  headline,
  direction = 'bottom',
  blockScrolling = true,
  optionalData,
  onClose,
  children,
  onClearAll,
  allExpanded,
  onToggleExpandAll,
}: React.PropsWithChildren<DrawerProps>) => {
  const { blockScroll } = useScrollBlock();
  const translate = useTranslations();
  useEffect(() => {
    if (blockScrolling) {
      blockScroll(isOpen);
    }
  }, [isOpen, blockScroll, blockScrolling]);

  const { ref } = useOnClickOutside(() => {
    !!isOpen && onClose?.();
  });

  const { drawerClassName: drawerWrapperClassName, drawerTransition } = useClassNames(direction as DrawerVariant);
  const { sortValues, currentSortValue, onSortValueChange, facets, onResetAll } = useProductList();

  const drawerHeaderClassName = classnames(headerClassName, 'p-4 lg:p-5');

  const drawerClassName = classnames(className, 'flex h-full  flex-col bg-white w-full');

  const handleClearClick = () => {
    if (typeof onClearAll === 'function') {
      onClearAll();
    } else {
      onResetAll();
    }
  };

  return (
    <>
      {isOpen && overlay && <Overlay zIndex="z-[345]" onClick={onClose} />}

      <Transition
        as="div"
        show={isOpen}
        enter="ease-out transition duration-100"
        enterFrom={drawerTransition.enterFrom}
        enterTo={drawerTransition.enterTo}
        leave="ease-in duration-75"
        leaveFrom={drawerTransition.leaveFrom}
        leaveTo={drawerTransition.leaveTo}
        className={drawerWrapperClassName + drawerWrapperClassName1}
        id="test"
      >
        <div ref={ref} className={drawerClassName}>
          <div className={drawerHeaderClassName}>
            <div className="flex justify-between">
              <div className="">
                <Typography
                  as="h2"
                  className="text-24 font-medium text-gray-700 lg:text-24"
                >
                  {headline}
                </Typography>
              </div>
              <div className="flex flex-1 items-center justify-end gap-3 md:gap-4">
                <div className="flex flex-shrink-0 items-center gap-3">
                  {onToggleExpandAll && (
                    <span
                      className="cursor-pointer text-14 font-normal leading-6 text-[#333333] underline whitespace-nowrap"
                      onClick={onToggleExpandAll}
                    >
                      {allExpanded === true ? 'Collapse All' : 'Expand All'}
                    </span>
                  )}
                  <span
                    className="cursor-pointer text-14 font-normal leading-6 text-[#333333] underline"
                    onClick={handleClearClick}
                  >
                    Clear All
                  </span>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}>
                  <XMarkIcon className="w-6 text-gray-700 lg:w-7" />
                </Button>
              </div>
            </div>
          </div>
          <div className="grow overflow-y-auto overscroll-none">{children}</div>
        </div>
      </Transition>
    </>
  );
};

export default Drawer;
